import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Building2, Phone, Mail, MapPin, User, Users as UsersIcon,
  Pencil, Trash2, Plus, StickyNote, BellRing, Printer,
} from "lucide-react";
import {
  categoriaLabel, statoLabel, statoBadgeClass, categoriaBadgeClass,
  formatDateIt, formatDateTimeIt, todayISODate, initials,
} from "@/lib/format";
import { CLI, REM } from "@/constants/testIds";
import ClientDialog from "@/components/app/ClientDialog";
import ReminderDialog from "@/components/app/ReminderDialog";
import { toast } from "sonner";

export default function ClienteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [remOpen, setRemOpen] = useState(false);
  const [editingRem, setEditingRem] = useState(null);
  const [delOpen, setDelOpen] = useState(false);

  const [noteText, setNoteText] = useState("");
  const [noteDate, setNoteDate] = useState(todayISODate());

  const load = useCallback(async () => {
    const [c, n, r] = await Promise.all([
      api.get(`/clients/${id}`),
      api.get(`/clients/${id}/notes`),
      api.get(`/reminders`, { params: { client_id: id } }),
    ]);
    setClient(c.data);
    setNotes(n.data);
    setReminders(r.data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!noteText.trim()) return toast.error("Inserisci il testo della nota");
    try {
      await api.post(`/clients/${id}/notes`, { data: noteDate, testo: noteText });
      setNoteText("");
      setNoteDate(todayISODate());
      toast.success("Nota aggiunta");
      load();
    } catch { toast.error("Errore salvataggio nota"); }
  };

  const deleteNote = async (nid) => {
    try {
      await api.delete(`/clients/${id}/notes/${nid}`);
      toast.success("Nota eliminata");
      load();
    } catch { toast.error("Errore"); }
  };

  const toggleReminder = async (rid) => {
    try {
      await api.post(`/reminders/${rid}/toggle`);
      load();
    } catch { toast.error("Errore"); }
  };

  const deleteReminder = async (rid) => {
    try {
      await api.delete(`/reminders/${rid}`);
      toast.success("Richiamo eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const deleteClient = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente eliminato");
      navigate("/clienti");
    } catch { toast.error("Errore"); }
  };

  if (!client) return <div className="p-8 text-stone-400">Caricamento cliente…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px]">
      <button
        onClick={() => navigate("/clienti")}
        className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Torna ai clienti
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-stone-900 text-white font-semibold flex items-center justify-center text-lg shrink-0">
            {initials(client.ragione_sociale) || <Building2 className="w-6 h-6" />}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge variant="outline" className={`${categoriaBadgeClass(client.categoria)} text-xs`}>
                {categoriaLabel(client.categoria)}
              </Badge>
              <Badge variant="outline" className={`${statoBadgeClass(client.stato)} text-xs`}>
                {statoLabel(client.stato)}
              </Badge>
              {client.operatore_commerciale && (
                <Badge variant="outline" className="bg-stone-900 text-white border-stone-900 text-xs">
                  {client.operatore_commerciale}
                </Badge>
              )}
            </div>
            <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-stone-900 max-w-2xl">
              {client.ragione_sociale}
            </h1>
            {client.citta && (
              <div className="text-stone-500 mt-2 flex items-center gap-1.5">
                <MapPin className="w-4 h-4" /> {client.sede && `${client.sede}, `}{client.citta}
                {client.provincia && ` (${client.provincia})`}{client.cap && ` — ${client.cap}`}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => window.print()}
            className="gap-2">
            <Printer className="w-4 h-4" /> Stampa
          </Button>
          <Button variant="outline" onClick={() => setEditOpen(true)} className="gap-2">
            <Pencil className="w-4 h-4" /> Modifica
          </Button>
          <Button
            variant="outline"
            onClick={() => setDelOpen(true)}
            data-testid={CLI.deleteBtn}
            className="gap-2 border-red-200 text-red-700 hover:bg-red-50">
            <Trash2 className="w-4 h-4" /> Elimina
          </Button>
        </div>
      </div>

      {/* Contact grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <ContactCard icon={Phone} title="Contatti Azienda" items={[
          { label: "Telefono", value: client.telefono },
          { label: "Email", value: client.email },
          { label: "Fax", value: client.fax },
        ]} />
        <ContactCard icon={User} title="Responsabile Acquisti" items={[
          { label: "Nome", value: client.resp_acquisti_nome },
          { label: "Telefono", value: client.resp_acquisti_telefono },
          { label: "Email", value: client.resp_acquisti_email },
        ]} />
        <ContactCard icon={UsersIcon} title="Referente Secondario" items={[
          { label: "Nome", value: client.referente_nome },
          { label: "Telefono", value: client.referente_telefono },
          { label: "Email", value: client.referente_email },
        ]} />
      </div>

      {client.note_generali && (
        <Card className="border-stone-200 shadow-sm rounded-2xl p-5 mb-6 bg-amber-50/50 border-amber-100">
          <div className="text-xs uppercase tracking-widest text-amber-800 font-semibold mb-2">Note Generali</div>
          <div className="text-stone-700 text-sm leading-relaxed whitespace-pre-wrap">{client.note_generali}</div>
        </Card>
      )}

      {/* Tabs: Note giornaliere / Richiami */}
      <Tabs defaultValue="note" data-testid={CLI.detailTabs}>
        <TabsList className="bg-stone-100">
          <TabsTrigger value="note" className="data-[state=active]:bg-white data-[state=active]:text-[#F56A1F] gap-2">
            <StickyNote className="w-4 h-4" /> Note Giornaliere ({notes.length})
          </TabsTrigger>
          <TabsTrigger value="richiami" className="data-[state=active]:bg-white data-[state=active]:text-[#F56A1F] gap-2">
            <BellRing className="w-4 h-4" /> Richiami ({reminders.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="note" className="mt-4">
          <Card className="border-stone-200 shadow-sm rounded-2xl p-5 mb-4">
            <div className="text-xs uppercase tracking-widest text-[#F56A1F] font-semibold mb-3">
              Aggiungi nota giornaliera
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-3">
              <Input
                data-testid={CLI.noteDate}
                type="date"
                value={noteDate}
                onChange={(e) => setNoteDate(e.target.value)}
                className="bg-white"
              />
              <Textarea
                data-testid={CLI.noteText}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Cosa è stato detto oggi al cliente…"
                rows={2}
                className="bg-white resize-none"
              />
              <Button
                data-testid={CLI.noteSubmit}
                onClick={addNote}
                className="bg-[#F56A1F] hover:bg-[#E05206] text-white gap-2 self-start h-full min-h-10">
                <Plus className="w-4 h-4" /> Aggiungi
              </Button>
            </div>
          </Card>

          <div className="space-y-3">
            {notes.length === 0 ? (
              <Card className="border-dashed border-stone-200 rounded-2xl p-10 text-center text-stone-400">
                Nessuna nota registrata per questo cliente.
              </Card>
            ) : notes.map((n) => (
              <Card
                key={n.id}
                data-testid={CLI.noteRow(n.id)}
                className="border-stone-200 rounded-xl p-4 flex gap-4 hover:border-stone-300 transition-colors">
                <div className="w-16 shrink-0 text-center">
                  <div className="text-xs uppercase tracking-widest text-[#F56A1F] font-semibold">
                    {new Date(n.data).toLocaleDateString("it-IT", { month: "short" })}
                  </div>
                  <div className="text-2xl font-semibold text-stone-900 leading-none">
                    {new Date(n.data).getDate()}
                  </div>
                  <div className="text-xs text-stone-400 mt-0.5">{new Date(n.data).getFullYear()}</div>
                </div>
                <div className="flex-1 border-l border-stone-200 pl-4">
                  <div className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{n.testo}</div>
                  <div className="text-[11px] text-stone-400 mt-2">{formatDateIt(n.data)}</div>
                </div>
                <button
                  data-testid={CLI.noteDelete(n.id)}
                  onClick={() => deleteNote(n.id)}
                  className="text-stone-300 hover:text-red-500 self-start transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="richiami" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button
              onClick={() => { setEditingRem(null); setRemOpen(true); }}
              className="bg-[#F56A1F] hover:bg-[#E05206] text-white gap-2">
              <Plus className="w-4 h-4" /> Nuovo Richiamo
            </Button>
          </div>
          {reminders.length === 0 ? (
            <Card className="border-dashed border-stone-200 rounded-2xl p-10 text-center text-stone-400">
              Nessun richiamo programmato per questo cliente.
            </Card>
          ) : (
            <div className="space-y-2">
              {reminders.map((r) => (
                <Card
                  key={r.id}
                  data-testid={REM.row(r.id)}
                  className={`border-stone-200 rounded-xl p-4 flex items-start gap-3 hover:border-stone-300 transition-colors ${
                    r.completed ? "opacity-60" : ""
                  }`}>
                  <button
                    data-testid={REM.toggle(r.id)}
                    onClick={() => toggleReminder(r.id)}
                    className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      r.completed ? "bg-emerald-500 border-emerald-500" : "border-stone-300 hover:border-[#F56A1F]"
                    }`}
                  >
                    {r.completed && <span className="text-white text-xs">✓</span>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium ${r.completed ? "line-through text-stone-400" : "text-stone-900"}`}>
                      {r.titolo}
                    </div>
                    {r.descrizione && <div className="text-sm text-stone-500 mt-1">{r.descrizione}</div>}
                    <div className="text-xs text-stone-400 font-mono mt-1">{formatDateTimeIt(r.scheduled_at)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={REM.edit(r.id)}
                      onClick={() => { setEditingRem(r); setRemOpen(true); }}
                      className="text-stone-400 hover:text-stone-900 p-1.5 rounded hover:bg-stone-100">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteReminder(r.id)}
                      className="text-stone-400 hover:text-red-500 p-1.5 rounded hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ClientDialog open={editOpen} onOpenChange={setEditOpen} initial={client} onSaved={load} />
      <ReminderDialog
        open={remOpen}
        onOpenChange={(v) => { setRemOpen(v); if (!v) setEditingRem(null); }}
        initial={editingRem}
        defaultClientId={id}
        onSaved={load}
      />

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione eliminerà definitivamente il cliente <b>{client.ragione_sociale}</b>,
              le sue note giornaliere e i richiami associati. Non è possibile annullare.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={deleteClient} className="bg-red-600 hover:bg-red-700">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ContactCard({ icon: Icon, title, items }) {
  const filled = items.filter((i) => i.value);
  return (
    <Card className="border-stone-200 shadow-sm rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#F56A1F]/10 text-[#F56A1F] flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{title}</div>
      </div>
      {filled.length === 0 ? (
        <div className="text-sm text-stone-300">— nessun dato —</div>
      ) : (
        <div className="space-y-2">
          {filled.map((i) => (
            <div key={i.label}>
              <div className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold">{i.label}</div>
              <div className="text-sm text-stone-800 break-words">{i.value}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
